import { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useWatchAuth } from '@/hooks/useWatchAuth'
import { api, WebPlayerChannel } from '@/lib/api'
import VideoPlayer from '@/components/VideoPlayer'
import CastPanel from '@/components/CastPanel'
import { useTVScene } from '@/hooks/useTVScene'
import {
  Search,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Play,
  Tv,
  Grid2X2,
  Square,
  Columns2,
  Menu,
  X
} from 'lucide-react'

type LayoutMode = 1 | 2 | 4

interface ActivePlayer {
  channel: WebPlayerChannel
  slot: number
}

export default function Watch() {
  const { isAuthenticated, isLoading, checkAuth, logout, user, getStreamUrl } = useWatchAuth()
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [activePlayers, setActivePlayers] = useState<ActivePlayer[]>([])
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [layoutMode, setLayoutMode] = useState<LayoutMode>(4)
  const [isMobile, setIsMobile] = useState(false)

  // TV Scene store for casting
  const { setLayout, addTile, removeTile, clearTiles } = useTVScene()

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768
      setIsMobile(mobile)
      if (mobile) {
        setSidebarOpen(false)
      }
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/watch/login')
    }
  }, [isAuthenticated, isLoading, navigate])

  const { data: channels = [], isLoading: channelsLoading, error } = useQuery({
    queryKey: ['webPlayerChannels'],
    queryFn: () => api.getWebPlayerChannels(),
    enabled: isAuthenticated,
    refetchInterval: 60000,
  })

  // Extract unique categories
  const categories = useMemo(() => {
    const cats = new Set<string>()
    channels.forEach(ch => {
      if (ch.category) cats.add(ch.category)
    })
    return Array.from(cats).sort()
  }, [channels])

  // Filter channels based on search and category
  const filteredChannels = useMemo(() => {
    return channels.filter(ch => {
      const matchesSearch = searchQuery === '' ||
        ch.name.toLowerCase().includes(searchQuery.toLowerCase())
      const matchesCategory = selectedCategory === 'all' ||
        ch.category === selectedCategory
      return matchesSearch && matchesCategory
    })
  }, [channels, searchQuery, selectedCategory])

  // Sync active players with TV Scene store for casting
  const syncTVScene = useCallback((players: ActivePlayer[], layout: LayoutMode) => {
    const layoutMap: Record<LayoutMode, 'single' | 'dual' | 'quad'> = {
      1: 'single',
      2: 'dual',
      4: 'quad',
    }
    setLayout(layoutMap[layout])
    clearTiles()
    players.forEach(p => {
      addTile({
        slot: p.slot,
        channelId: p.channel.id,
        channelName: p.channel.name,
        streamUrl: p.channel.stream_url,
        iconUrl: p.channel.icon_url,
      })
    })
  }, [setLayout, clearTiles, addTile])

  const handleChannelClick = (channel: WebPlayerChannel) => {
    const existingIndex = activePlayers.findIndex(p => p.channel.id === channel.id)
    if (existingIndex >= 0) return

    const maxSlots = layoutMode
    let newPlayers: ActivePlayer[]

    if (activePlayers.length < maxSlots) {
      const usedSlots = activePlayers.map(p => p.slot)
      const availableSlots = Array.from({ length: maxSlots }, (_, i) => i)
      const nextSlot = availableSlots.find(s => !usedSlots.includes(s)) ?? 0
      newPlayers = [...activePlayers, { channel, slot: nextSlot }]
    } else {
      const trimmedPlayers = [...activePlayers.slice(1)]
      const usedSlots = trimmedPlayers.map(p => p.slot)
      const availableSlots = Array.from({ length: maxSlots }, (_, i) => i)
      const nextSlot = availableSlots.find(s => !usedSlots.includes(s)) ?? 0
      newPlayers = [...trimmedPlayers, { channel, slot: nextSlot }]
    }

    setActivePlayers(newPlayers)
    syncTVScene(newPlayers, layoutMode)

    // Close sidebar on mobile after selection
    if (isMobile) {
      setSidebarOpen(false)
    }
  }

  const handleClosePlayer = (channelId: string) => {
    // Find the player being closed to get its stream URL
    const player = activePlayers.find(p => p.channel.id === channelId)
    if (player) {
      // Notify server to stop the stream (best-effort, fire-and-forget)
      const streamUrl = getStreamUrl(player.channel.stream_url)
      if (!streamUrl.includes('{password}')) {
        api.stopPlayback(streamUrl)
      }
      removeTile(player.slot)
    }
    const newPlayers = activePlayers.filter(p => p.channel.id !== channelId)
    setActivePlayers(newPlayers)
  }

  const handleLogout = () => {
    logout()
    navigate('/watch/login')
  }

  const isChannelPlaying = (channelId: string) => {
    return activePlayers.some(p => p.channel.id === channelId)
  }

  const handleLayoutChange = (newLayout: LayoutMode) => {
    setLayoutMode(newLayout)
    let newPlayers = activePlayers

    // Remove players that exceed the new layout limit
    if (activePlayers.length > newLayout) {
      // Stop streams for removed players
      const removedPlayers = activePlayers.slice(newLayout)
      removedPlayers.forEach(player => {
        const streamUrl = getStreamUrl(player.channel.stream_url)
        if (!streamUrl.includes('{password}')) {
          api.stopPlayback(streamUrl)
        }
        removeTile(player.slot)
      })
      newPlayers = activePlayers.slice(0, newLayout)
      setActivePlayers(newPlayers)
    }

    syncTVScene(newPlayers, newLayout)
  }

  // Cleanup: stop all streams when component unmounts or page unloads
  useEffect(() => {
    const stopAllStreams = () => {
      activePlayers.forEach(player => {
        const streamUrl = getStreamUrl(player.channel.stream_url)
        if (!streamUrl.includes('{password}')) {
          // Use sendBeacon for reliable delivery during page unload
          const stopUrl = streamUrl.replace('.m3u8', '/stop')
          navigator.sendBeacon(stopUrl)
        }
      })
    }

    // Handle tab/window close
    window.addEventListener('beforeunload', stopAllStreams)

    return () => {
      window.removeEventListener('beforeunload', stopAllStreams)
      stopAllStreams()
    }
  }, [activePlayers, getStreamUrl])

  // Get grid classes based on layout (mobile-responsive)
  const getGridClasses = () => {
    switch (layoutMode) {
      case 1:
        return 'grid-cols-1 grid-rows-1'
      case 2:
        // Stack vertically on mobile, horizontal on larger screens
        return 'grid-cols-1 md:grid-cols-2 grid-rows-2 md:grid-rows-1'
      case 4:
      default:
        // 2x2 grid on all sizes, but with adjusted aspect ratio on mobile
        return 'grid-cols-2 grid-rows-2'
    }
  }

  // Render grid slots
  const renderGrid = () => {
    const slots = Array.from({ length: layoutMode }, (_, i) => i)
    return slots.map(slot => {
      const player = activePlayers.find(p => p.slot === slot)

      if (player) {
        const streamUrl = getStreamUrl(player.channel.stream_url)
        if (streamUrl.includes('{password}')) {
          return (
            <div key={slot} className="flex items-center justify-center bg-black">
              <div className="text-center text-gray-500">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                <p className="text-sm">Loading...</p>
              </div>
            </div>
          )
        }
        return (
          <div key={slot} className="relative bg-black overflow-hidden">
            <VideoPlayer
              src={streamUrl}
              channelName={player.channel.name}
              channelIcon={player.channel.icon_url}
              onClose={() => handleClosePlayer(player.channel.id)}
              index={activePlayers.indexOf(player)}
            />
          </div>
        )
      }

      return (
        <div
          key={slot}
          className="flex items-center justify-center bg-black"
        >
          <div className="text-center text-gray-700">
            <Tv className="w-16 h-16 mx-auto mb-3 opacity-20" />
            <p className="text-sm opacity-40">Select a channel</p>
          </div>
        </div>
      )
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-black">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="flex h-screen bg-black text-white overflow-hidden">
      {/* Mobile overlay */}
      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-20"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Fixed drawer on mobile, relative on desktop */}
      <aside
        className={`flex flex-col bg-sidebar border-r border-sidebar-muted transition-all duration-300 z-30 ${
          isMobile
            ? `fixed inset-y-0 left-0 w-80 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`
            : sidebarOpen ? 'w-80' : 'w-0 overflow-hidden'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-sidebar-muted">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="OBACast" className="h-10 w-auto" />
          </div>
          <div className="flex items-center gap-2">
            {/* Cast Panel */}
            <CastPanel className="hidden sm:flex" />
            <button
              onClick={handleLogout}
              className="p-2 rounded-lg hover:bg-sidebar-muted text-sidebar-foreground/60 hover:text-sidebar-foreground transition-colors"
              title="Logout"
            >
              <LogOut className="w-5 h-5" />
            </button>
            {isMobile && (
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-2 rounded-lg hover:bg-sidebar-muted text-sidebar-foreground/60 hover:text-sidebar-foreground transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        {/* Layout Switcher */}
        <div className="px-4 pt-4 pb-2">
          <div className="flex items-center gap-1 p-1 bg-sidebar-muted rounded-lg">
            <button
              onClick={() => handleLayoutChange(1)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                layoutMode === 1
                  ? 'bg-primary text-primary-foreground'
                  : 'text-sidebar-foreground/70 hover:text-sidebar-foreground'
              }`}
            >
              <Square className="w-4 h-4" />
              <span>1</span>
            </button>
            <button
              onClick={() => handleLayoutChange(2)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                layoutMode === 2
                  ? 'bg-primary text-primary-foreground'
                  : 'text-sidebar-foreground/70 hover:text-sidebar-foreground'
              }`}
            >
              <Columns2 className="w-4 h-4" />
              <span>2</span>
            </button>
            <button
              onClick={() => handleLayoutChange(4)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                layoutMode === 4
                  ? 'bg-primary text-primary-foreground'
                  : 'text-sidebar-foreground/70 hover:text-sidebar-foreground'
              }`}
            >
              <Grid2X2 className="w-4 h-4" />
              <span>4</span>
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="p-4 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-sidebar-foreground/50" />
            <input
              type="text"
              placeholder="Search channels..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-10 pl-10 pr-4 bg-sidebar-muted border border-sidebar-muted rounded-lg text-sm text-sidebar-foreground placeholder:text-sidebar-foreground/50 focus:outline-none focus:border-primary transition-colors"
            />
          </div>

          {/* Category Filter */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="w-full h-10 px-3 bg-sidebar-muted border border-sidebar-muted rounded-lg text-sm text-sidebar-foreground focus:outline-none focus:border-primary transition-colors"
          >
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        {/* Active Players Count */}
        <div className="px-4 pb-3">
          <div className="flex items-center gap-2 text-sm text-sidebar-foreground/60">
            <Play className="w-4 h-4" />
            <span>{activePlayers.length}/{layoutMode} players active</span>
          </div>
        </div>

        {/* Channel List */}
        <div className="flex-1 overflow-y-auto px-2 pb-4">
          {channelsLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-400 text-sm">
              Failed to load channels
            </div>
          ) : filteredChannels.length === 0 ? (
            <div className="text-center py-8 text-sidebar-foreground/50 text-sm">
              No channels found
            </div>
          ) : (
            <div className="space-y-1">
              {filteredChannels.map(channel => {
                const isPlaying = isChannelPlaying(channel.id)
                return (
                  <button
                    key={channel.id}
                    onClick={() => handleChannelClick(channel)}
                    disabled={isPlaying}
                    className={`w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors ${
                      isPlaying
                        ? 'bg-primary/20 border border-primary/50 cursor-default'
                        : 'hover:bg-sidebar-muted'
                    }`}
                  >
                    {channel.icon_url ? (
                      <img
                        src={channel.icon_url}
                        alt=""
                        className="w-10 h-10 rounded object-cover bg-sidebar-muted flex-shrink-0"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none'
                        }}
                      />
                    ) : (
                      <div className="w-10 h-10 rounded bg-sidebar-muted flex items-center justify-center flex-shrink-0">
                        <Tv className="w-5 h-5 text-sidebar-foreground/50" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sidebar-foreground truncate">{channel.name}</span>
                        {isPlaying && (
                          <span className="px-1.5 py-0.5 bg-red-500 text-white text-xs font-medium rounded">
                            LIVE
                          </span>
                        )}
                      </div>
                      {channel.category && (
                        <span className="text-xs text-sidebar-foreground/60 truncate block">
                          {channel.category}
                        </span>
                      )}
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* User Info */}
        {user && (
          <div className="p-4 border-t border-sidebar-muted text-sm text-sidebar-foreground/60">
            Logged in as <span className="text-sidebar-foreground">{user.username}</span>
          </div>
        )}
      </aside>

      {/* Sidebar Toggle - Desktop only */}
      {!isMobile && (
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="absolute top-1/2 -translate-y-1/2 z-10 p-1 bg-sidebar border border-sidebar-muted rounded-r-lg hover:bg-sidebar-muted transition-all duration-300"
          style={{ left: sidebarOpen ? '320px' : 0 }}
        >
          {sidebarOpen ? (
            <ChevronLeft className="w-5 h-5 text-sidebar-foreground" />
          ) : (
            <ChevronRight className="w-5 h-5 text-sidebar-foreground" />
          )}
        </button>
      )}

      {/* Main Content - Dynamic Grid */}
      <main className="flex-1 bg-black relative">
        {/* Mobile header with menu button and cast */}
        {isMobile && (
          <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-3 bg-gradient-to-b from-black/80 to-transparent">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-lg bg-black/50 hover:bg-black/70 text-white transition-colors"
            >
              <Menu className="w-5 h-5" />
            </button>
            <CastPanel />
          </div>
        )}

        <div className={`grid ${getGridClasses()} h-full`}>
          {renderGrid()}
        </div>
      </main>
    </div>
  )
}
